import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import MaterialRow from '../MaterialRow';
import { getBondByLabel, computeSheet, buildDefaultMaterialPlan } from '../../../utils/calculations';

/**
 * SendToProductionModal
 *
 * Props:
 *   sheet     — the full sheet object from the list (has inputs, clientName, sheetNumber)
 *   isOpen    — bool
 *   onClose   — () => void
 *   onSuccess — () => void  — called after successful production, triggers list refresh
 */
const SendToProductionModal = ({ sheet, isOpen, onClose, onSuccess }) => {
    const [materialPlan, setMaterialPlan] = useState([]);
    const [sending, setSending]           = useState(false);

    // Build default material plan from sheet inputs when modal opens
    useEffect(() => {
        if (!isOpen || !sheet) return;

        const bond = getBondByLabel(sheet.inputs?.bondType || '');
        if (bond) {
            const computed = computeSheet(sheet.inputs, bond);
            if (computed) {
                setMaterialPlan(buildDefaultMaterialPlan(
                    computed,
                    sheet.inputs.gritCode,
                    sheet.inputs.bondType
                ));
                return;
            }
        }
        // Fallback: empty plan for manual entry
        setMaterialPlan([]);
    }, [isOpen, sheet]);

    if (!isOpen || !sheet) return null;

    // ── Validation ──────────────────────────────────────────────
    const rowsWithQty    = materialPlan.filter(r => parseFloat(r.quantityUsed) > 0);
    const unlinkedRows   = rowsWithQty.filter(r => !r.itemId);
    const canConfirm     = rowsWithQty.length > 0 && unlinkedRows.length === 0;

    // ── Handlers ────────────────────────────────────────────────
    const handleChange = (index, updatedEntry) => {
        setMaterialPlan(prev => prev.map((m, i) => i === index ? updatedEntry : m));
    };

    const handleRemove = (index) => {
        setMaterialPlan(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddRow = () => {
        setMaterialPlan(prev => [
            ...prev,
            { itemId: null, itemName: '', quantityUsed: '', unit: 'gm' }
        ]);
    };

    const handleConfirm = async () => {
        setSending(true);
        try {
            const payload = {
                materialPlan: rowsWithQty.map(r => ({
                    itemId:       r.itemId,
                    itemName:     r.itemName,
                    quantityUsed: parseFloat(r.quantityUsed),
                    unit:         r.unit || 'gm',
                })),
            };

            await api.post(`/process-sheets/${sheet._id}/send-to-production`, payload);

            toast.success(
                `${sheet.sheetNumber} sent to production · Inventory updated`,
                { autoClose: 6000 }
            );
            onSuccess?.();
            onClose();
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors?.length) {
                errors.forEach(e => toast.error(e, { autoClose: 8000 }));
            } else {
                toast.error(err.response?.data?.message || 'Failed to send to production');
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

                {/* ── Header ──────────────────────────────────────── */}
                <div className="flex items-center justify-between px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">Send to Production</h2>
                        <p className="text-xs text-gray-400 mt-0.5">
                            <span className="font-mono font-semibold text-gray-600">{sheet.sheetNumber}</span>
                            &nbsp;·&nbsp;{sheet.clientName}
                            &nbsp;·&nbsp;{sheet.inputs?.gritCode}-{sheet.inputs?.bondType}-{sheet.inputs?.concentration}
                            &nbsp;·&nbsp;Batch {sheet.inputs?.batchNumber}
                        </p>
                    </div>
                    <button onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none transition-colors">
                        ×
                    </button>
                </div>

                {/* ── Body ────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-6 py-2 space-y-2">

                    {/* Info banner */}
                    <div className="w-fit bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 text-xs text-amber-700">
                        <strong>Review carefully.</strong> Confirming will deduct the listed quantities from inventory.
                    </div>

                    {/* Column headers */}
                    <div className="grid grid-cols-12 gap-2 px-3">
                        <div className="col-span-5 text-xs text-gray-400 font-medium uppercase tracking-wide">Item</div>
                        <div className="col-span-4 text-xs text-gray-400 font-medium uppercase tracking-wide">Qty</div>
                        <div className="col-span-2 text-xs text-gray-400 font-medium uppercase tracking-wide">Unit</div>
                        <div className="col-span-1" />
                    </div>

                    {/* Material rows */}
                    {materialPlan.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">
                            No items yet. Click "+ Add row" to add material entries.
                        </p>
                    )}

                    {materialPlan.map((entry, i) => (
                        <div key={entry._key || entry.itemId || i}>
                            <MaterialRow
                                entry={entry}
                                index={i}
                                onChange={handleChange}
                                onRemove={handleRemove}
                                locked={false}
                            />
                            {/* <div className="px-3 -mt-1 mb-2 flex items-center gap-3 flex-wrap">
                                Unlinked warning
                                {parseFloat(entry.quantityUsed) > 0 && !entry.itemId && (
                                    <span className="text-xs text-red-500">
                                        ⚠ Not linked — search and select an inventory item
                                    </span>
                                )}
                            </div> */}
                        </div>
                    ))}

                    {/* Add row */}
                    <button type="button" onClick={handleAddRow}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                        + Add row
                    </button>
                </div>

                {/* ── Footer ──────────────────────────────────────── */}
                <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
                    {/* Summary of what will be deducted */}
                    {rowsWithQty.length > 0 && (
                        <p className="text-xs text-gray-400 mb-3">
                            {rowsWithQty.length} item{rowsWithQty.length > 1 ? 's' : ''} will be deducted from inventory.
                            {unlinkedRows.length > 0 && (
                                <span className="text-red-500 ml-1">
                                    {unlinkedRows.length} row{unlinkedRows.length > 1 ? 's' : ''} still unlinked.
                                </span>
                            )}
                        </p>
                    )}

                    <div className="flex gap-3">
                        <button type="button" onClick={onClose}
                            className="flex-1 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors">
                            Cancel
                        </button>
                        <button type="button" onClick={handleConfirm}
                            disabled={!canConfirm || sending}
                            title={!canConfirm ? 'Link all items to inventory before confirming' : ''}
                            className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors">
                            {sending ? 'Processing…' : '🚀 Confirm & Send'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendToProductionModal;